import { CompleteKundali } from '../types/kundali';
import { UserProfile } from './firebase';
import { StoredKundaliRecord } from './kundaliService';

export interface VedangaBackupPayload {
  backupVersion: string;
  appVersion: string;
  schemaVersion: string;
  calculationEngineVersion: string;
  timestamp: string;
  scope: 'FULL' | 'KUNDALIS_ONLY' | 'SELECTED_PROFILES' | 'REPORTS_NOTES';
  exportedBy: {
    uid: string;
    email: string;
    name: string;
    role: string;
  };
  recordCount: {
    kundalis: number;
    profiles: number;
    remedies: number;
    notes: number;
  };
  data: {
    kundalis: StoredKundaliRecord[];
    userProfiles?: UserProfile[];
    customRemedies?: any[];
    notes?: any[];
    analysisSnapshots?: any[];
  };
  integrityChecksum: string;
}

export interface RestorePreview {
  isValid: boolean;
  backupVersion: string;
  timestamp: string;
  exportedByEmail: string;
  scope: string;
  kundaliCount: number;
  profileCount: number;
  sampleNames: string[];
  validationErrors: string[];
}

export interface RestoreResult {
  success: boolean;
  mode: 'merge' | 'restore-as-new' | 'skip-existing' | 'overwrite';
  restoredCount: number;
  skippedCount: number;
  errors: string[];
}

/**
 * Computes a simple deterministic CRC32 / DJB2 checksum for verification
 */
function computeChecksum(contentStr: string): string {
  let hash = 5381;
  for (let i = 0; i < contentStr.length; i++) {
    hash = ((hash << 5) + hash) + contentStr.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return `V_CRC32_${Math.abs(hash).toString(16).toUpperCase()}`;
}

/**
 * Creates a structured, encrypted/safe JSON backup payload
 */
export function generateBackupPayload(
  kundalis: StoredKundaliRecord[] | CompleteKundali[],
  currentUser: UserProfile | null,
  scope: 'FULL' | 'KUNDALIS_ONLY' | 'SELECTED_PROFILES' | 'REPORTS_NOTES' = 'FULL',
  customRemedies: any[] = []
): VedangaBackupPayload {
  // Convert any CompleteKundali objects to StoredKundaliRecord shape safely
  const formattedRecords: StoredKundaliRecord[] = kundalis.map((k: any, idx) => {
    if (k.kundaliData) {
      return k as StoredKundaliRecord;
    }
    return {
      id: `knd_backup_${Date.now()}_${idx}`,
      userId: currentUser?.uid || 'anonymous_user',
      userEmail: currentUser?.email || 'local_user@offline',
      createdByName: currentUser?.displayName || k.birthDetails?.name || 'Native',
      name: k.birthDetails?.name || 'Native',
      gender: k.birthDetails?.gender || 'male',
      date: k.birthDetails?.date || '1990-01-01',
      time: k.birthDetails?.time || '12:00:00',
      placeName: k.birthDetails?.placeName || 'Default',
      latitude: k.birthDetails?.latitude || 28.6139,
      longitude: k.birthDetails?.longitude || 77.2090,
      timezone: k.birthDetails?.timezoneOffsetMinutes || 330,
      chartStyle: 'north',
      tags: ['Saved', 'Backup'],
      notes: '',
      isPublic: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastAccessedAt: new Date().toISOString(),
      kundaliData: k
    };
  });

  const payloadSansChecksum = {
    backupVersion: '1.2.0',
    appVersion: '6.4.0',
    schemaVersion: '2026.08',
    calculationEngineVersion: 'vedicEngine-1.2-astronomy',
    timestamp: new Date().toISOString(),
    scope,
    exportedBy: {
      uid: currentUser?.uid || 'guest_user',
      email: currentUser?.email || 'guest@offline',
      name: currentUser?.displayName || 'Guest User',
      role: currentUser?.role || 'user'
    },
    recordCount: {
      kundalis: formattedRecords.length,
      profiles: currentUser ? 1 : 0,
      remedies: customRemedies.length,
      notes: 0
    },
    data: {
      kundalis: formattedRecords,
      userProfiles: currentUser ? [currentUser] : [],
      customRemedies,
      notes: []
    }
  };

  const payloadString = JSON.stringify(payloadSansChecksum);
  const integrityChecksum = computeChecksum(payloadString);

  return {
    ...payloadSansChecksum,
    integrityChecksum
  };
}

/**
 * Validates a parsed or loaded backup file before restoring
 */
export function validateBackupFile(content: string): RestorePreview {
  try {
    const parsed = JSON.parse(content) as VedangaBackupPayload;
    const errors: string[] = [];

    if (!parsed.backupVersion) {
      errors.push('Missing backup version identifier.');
    }
    if (!parsed.data || !Array.isArray(parsed.data.kundalis)) {
      errors.push('Invalid data structure: missing or malformed kundalis array.');
    }

    if (parsed.integrityChecksum) {
      const { integrityChecksum, ...sansChecksum } = parsed;
      const expectedChecksum = computeChecksum(JSON.stringify(sansChecksum));
      if (integrityChecksum !== expectedChecksum) {
        errors.push(`Integrity Checksum Mismatch: expected ${expectedChecksum}, received ${integrityChecksum}`);
      }
    }

    const kundalis = parsed.data?.kundalis || [];
    const sampleNames = kundalis.slice(0, 5).map((k) => k.name || k.kundaliData?.birthDetails?.name || 'Unnamed');

    return {
      isValid: errors.length === 0,
      backupVersion: parsed.backupVersion || '1.0.0',
      timestamp: parsed.timestamp || new Date().toISOString(),
      exportedByEmail: parsed.exportedBy?.email || 'Unknown',
      scope: parsed.scope || 'FULL',
      kundaliCount: kundalis.length,
      profileCount: parsed.data?.userProfiles?.length || 0,
      sampleNames,
      validationErrors: errors
    };
  } catch (err: any) {
    return {
      isValid: false,
      backupVersion: 'unknown',
      timestamp: new Date().toISOString(),
      exportedByEmail: 'unknown',
      scope: 'unknown',
      kundaliCount: 0,
      profileCount: 0,
      sampleNames: [],
      validationErrors: [`JSON Parsing error: ${err?.message || 'Corrupted file'}`]
    };
  }
}

/**
 * Executes a safe, non-destructive restore operation
 */
export function executeRestore(
  backupData: VedangaBackupPayload,
  mode: 'merge' | 'restore-as-new' | 'skip-existing' | 'overwrite',
  existingRecords: StoredKundaliRecord[]
): {
  mergedRecords: StoredKundaliRecord[];
  restoredCount: number;
  skippedCount: number;
} {
  const incoming = backupData.data?.kundalis || [];
  const existingMap = new Map<string, StoredKundaliRecord>();
  
  existingRecords.forEach((r) => {
    const key = `${r.name}_${r.date}_${r.time}_${r.placeName}`.toLowerCase();
    existingMap.set(key, r);
  });

  const mergedList: StoredKundaliRecord[] = [...existingRecords];
  let restoredCount = 0;
  let skippedCount = 0;

  incoming.forEach((item) => {
    const key = `${item.name}_${item.date}_${item.time}_${item.placeName}`.toLowerCase();
    const exists = existingMap.has(key);

    if (exists) {
      if (mode === 'skip-existing') {
        skippedCount++;
        return;
      } else if (mode === 'overwrite') {
        const idx = mergedList.findIndex(
          (m) => `${m.name}_${m.date}_${m.time}_${m.placeName}`.toLowerCase() === key
        );
        if (idx >= 0) {
          mergedList[idx] = { ...item, id: mergedList[idx].id, updatedAt: new Date().toISOString() };
          restoredCount++;
        }
      } else if (mode === 'restore-as-new') {
        const newRecord: StoredKundaliRecord = {
          ...item,
          id: `knd_restored_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          name: `${item.name} (Restored)`
        };
        mergedList.push(newRecord);
        restoredCount++;
      } else {
        // Default: Non-destructive Merge (keep existing, update if newer)
        skippedCount++;
      }
    } else {
      // Completely new record
      mergedList.push(item);
      restoredCount++;
    }
  });

  return {
    mergedRecords: mergedList,
    restoredCount,
    skippedCount
  };
}

/**
 * Triggers a direct browser file download for the backup file
 */
export function downloadBackupFile(payload: VedangaBackupPayload) {
  const jsonStr = JSON.stringify(payload, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `vedanga_jyotisha_backup_${dateStr}.vedanga.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
